/**
 * useKeyboardNav — global keyboard shortcut handler.
 *
 * Shortcuts:
 *   Cmd+K / Ctrl+K  → open command palette
 *   G then D        → go to CFO Dashboard
 *   G then E        → go to Activity Tracker (exceptions)
 *   G then W        → go to Workflows
 *   G then R        → go to Financial Reports
 *   G then C        → go to Command Center
 *   ?               → show shortcuts help
 *   Escape          → close any open panel
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LEADER_TIMEOUT = 500; // ms to wait for second key after 'G'

const GO_SHORTCUTS: Record<string, string> = {
  d: '/cfo-dashboard',
  e: '/activity-tracker',
  w: '/workflows',
  r: '/financial-reports',
  c: '/',
  o: '/operations',
  a: '/analytics',
  g: '/governance',
  i: '/intelligence',
};

interface UseKeyboardNavReturn {
  isCommandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  isShortcutsHelpOpen: boolean;
  setShortcutsHelpOpen: (open: boolean) => void;
}

export function useKeyboardNav(): UseKeyboardNavReturn {
  const navigate = useNavigate();
  const [isCommandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isShortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const leaderRef = useRef(false);
  const leaderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isInputFocused = useCallback(() => {
    const el = document.activeElement;
    if (!el) return false;
    const tag = el.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if ((el as HTMLElement).contentEditable === 'true') return true;
    return false;
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Cmd+K / Ctrl+K → command palette (works even in inputs)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
        return;
      }

      // Escape → close panels
      if (e.key === 'Escape') {
        if (isCommandPaletteOpen) setCommandPaletteOpen(false);
        if (isShortcutsHelpOpen) setShortcutsHelpOpen(false);
        return;
      }

      // Skip all other shortcuts if typing in an input
      if (isInputFocused()) return;

      // ? → show shortcuts help
      if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setShortcutsHelpOpen(prev => !prev);
        return;
      }

      // G leader key
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !leaderRef.current) {
        leaderRef.current = true;
        if (leaderTimerRef.current) clearTimeout(leaderTimerRef.current);
        leaderTimerRef.current = setTimeout(() => {
          leaderRef.current = false;
        }, LEADER_TIMEOUT);
        return;
      }

      // G + second key → navigate
      if (leaderRef.current) {
        leaderRef.current = false;
        if (leaderTimerRef.current) clearTimeout(leaderTimerRef.current);
        const dest = GO_SHORTCUTS[e.key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          navigate(dest);
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      if (leaderTimerRef.current) clearTimeout(leaderTimerRef.current);
    };
  }, [navigate, isInputFocused, isCommandPaletteOpen, isShortcutsHelpOpen]);

  return { isCommandPaletteOpen, setCommandPaletteOpen, isShortcutsHelpOpen, setShortcutsHelpOpen };
}
